import { MonitorIcon, MoonIcon, SunIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Segmented } from '../components/ui/toggle-group';
import { SimpleTooltip } from '../components/ui/tooltip';
import { useThemeMode, type ThemeMode } from './useThemeMode';

export interface ThemeToggleProps {
  /**
   * 'switch' — a single icon button that flips light/dark (compact, for headers).
   * 'segmented' — light / system / dark, for a settings page.
   */
  variant?: 'switch' | 'segmented';
}

export function ThemeToggle({ variant = 'switch' }: ThemeToggleProps) {
  const { mode, resolved, setMode, toggle } = useThemeMode();

  if (variant === 'segmented') {
    return (
      <Segmented<ThemeMode>
        value={mode}
        onChange={setMode}
        options={[
          { value: 'light', icon: <SunIcon />, label: 'Light' },
          { value: 'system', icon: <MonitorIcon />, label: 'System' },
          { value: 'dark', icon: <MoonIcon />, label: 'Dark' },
        ]}
      />
    );
  }

  // The glyph shows the theme you would switch *to*: a moon on a light page,
  // a sun on a dark one.
  return (
    <SimpleTooltip content={resolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
      <Button variant="ghost" size="icon" aria-label="Toggle colour theme" onClick={toggle}>
        {resolved === 'dark' ? <SunIcon /> : <MoonIcon />}
      </Button>
    </SimpleTooltip>
  );
}
